import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { List, useDynamicRowHeight } from "react-window";
import {
    useTheme,
    useMediaQuery,
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Stack,
    Divider,
    // Tooltip,
} from "@mui/material";
import { WarningAmber } from "@mui/icons-material";
import api from "state/axios";
import { splitString, formatTZDate } from "state/helpers";
import { hasPermission } from "state/checkPermission";
import FlexBetween from "components/FlexBetween";
import RouteCrewListDialog from "components/RouteCrewListDialog";


/* ============ ResizeObserver (throttled) ============ */
const useResizeObserver = (ref, onResize, delay = 100) => {
    const timeoutRef = useRef(null);
    const lastRectRef = useRef(null);

    useEffect(() => {
        if (!ref.current) return;

        const observer = new ResizeObserver((entries) => {
            const rect = entries[0]?.contentRect;
            if (!rect) return;

            lastRectRef.current = rect;
            if (timeoutRef.current) return;

            timeoutRef.current = setTimeout(() => {
                onResize(lastRectRef.current);
                timeoutRef.current = null;
            }, delay);
        });

        observer.observe(ref.current);

        return () => {
            observer.disconnect();
            clearTimeout(timeoutRef.current);
        };
    }, [ref, onResize, delay]);
};

const truncateAtSeparator = (str, max = 25) => {
    if (!str || str.length <= max) return str;
    const sub = str.slice(0, max);
    const lastSep = Math.max(sub.lastIndexOf('-'), sub.lastIndexOf('/'));
    return (lastSep > 0 ? sub.slice(0, lastSep) : sub) + '…';
};

/* ================= Row ================= */
const RowComponent = React.memo(function RowComponent({
    index,
    style,
    timeMode,
    theme,
    canView,
    minRouteTAT,
    routes,
    onOpenCrewList,
}) {
    const route = routes[index];
    if (!route) return null;

    const getTime = (flight, field) => timeMode === "UTC"
        ? flight[field]
        : flight[`${field}LocalBase`];


    return (
        <div style={{ ...style }}>
            <Card variant="outlined" sx={{ overflowX: "auto" }}>
                <CardContent                                  
                    sx={{    
                        px: 1,
                        pt: 1,
                        "&:last-child": { pb: 1 },
                    }}
                >                   
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6" color={theme.palette.primary.dark}>
                            {formatTZDate(getTime(route, "routeDate"), 'UTC', "dd/MM/yy")}
                        </Typography>

                        <Typography
                            variant="h6"
                            color={theme.palette.primary.dark}
                            sx={{
                                lineHeight: 1,
                                flex: 1,
                                minWidth: 0,
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {truncateAtSeparator(route.crewRoute, 35)}
                        </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                textDecoration: canView ? "underline" : "none",
                                cursor: canView ? "pointer" : "text",
                            }}
                            onClick={canView ? () => onOpenCrewList(route) : undefined}
                        >
                            Route No: {route.routeNo}
                        </Typography>

                        <Divider
                            orientation="vertical"
                            flexItem
                        />

                        <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                            }}
                        >
                            Flight No: {truncateAtSeparator(route.flightNo, 30)}
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 0.2 }} />

                    <Stack spacing={0.2}>
                        {route.routeDetails.map((f, i) => {
                            const showHighlight = f.diffRegEstimatedTat && f.diffRegEstimatedTat < minRouteTAT;

                            return (
                                <FlexBetween
                                    key={i}
                                    gap={0.5}
                                    borderRadius={1}
                                    bgcolor={showHighlight 
                                        ? "error.lighter" 
                                        : "background.default"
                                    }
                                    border={showHighlight
                                        ? `1px dotted ${theme.palette.neutral.deep}`
                                        : "1px solid transparent"
                                    }
                                >
                                    <Typography width={36} textAlign="center">
                                        {timeMode === "UTC"
                                            ? formatTZDate(f.fullAtd || f.fullEtd, 'UTC', "dd/MM")
                                            : formatTZDate(f.fullAtdLocalBase || f.fullEtdLocalBase, 'UTC', "dd/MM")
                                        }
                                    </Typography>

                                    <Typography width={36} textAlign="center">
                                        {splitString(f.flightReg, "-", 1) || f.flightReg}
                                    </Typography>

                                    <Typography width={32} align="right">
                                        {f.flightNo}
                                    </Typography>

                                    <Typography width={36} textAlign="center">
                                        {getTime(f, f.atd ? "atd" : "etd")}
                                    </Typography>

                                    <Typography width={32} textAlign="center">
                                        {f.dep}
                                    </Typography>

                                    <Typography width={32} textAlign="center">
                                        {f.arr}
                                    </Typography>

                                    <Typography width={36} textAlign="center">
                                        {getTime(f, f.ata ? "ata" : "eta")}
                                    </Typography>

                                    <Typography width={6} textAlign="center">
                                        {f.pax}
                                    </Typography>

                                    <Box minWidth={60} textAlign="center">
                                        {showHighlight && (
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                color="warning"
                                                label={`TAT ${f.diffRegActualTat || f.diffRegEstimatedTat}'`}
                                            />
                                        )}
                                    </Box>
                                </FlexBetween>
                            );
                        })}
                    </Stack>
                </CardContent>
            </Card>
        </div>
    );
});


/* ================= Main ================= */
const ShortTurnaroundCrewRoutes = ({ crewRoutes = [] }) => {
    const user = useSelector((state) => state.user); 
    const timeMode = useSelector((state) => state.timemode);  
    const params = useSelector((state) => state.params);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const canView = hasPermission(user?.permissions, "MainPage", "view", false);

    const listRef = useRef(null);
    const ROW_FALLBACK = 120;
    const rowHeight = useDynamicRowHeight({
        defaultRowHeight: ROW_FALLBACK,
    });

    const [listHeight, setListHeight] = useState(400);

    useResizeObserver(listRef, (rect) => {
        setListHeight(rect.height);
    });

    const minRouteTAT = Number(
        params?.find((p) => p.param === "MINIMUM_CREW_ROUTE_TAT_MINUTES")
        ?.value || 80
    );

    const filteredRoutes = useMemo(() => 
        crewRoutes.filter((r) =>
        r.routeDetails.some(
            (i) => i.diffRegEstimatedTat && i.diffRegEstimatedTat < minRouteTAT
        )
    ), [crewRoutes, minRouteTAT]);

    const [openDialog, setOpenDialog] = useState(false);
    const [crewList, setCrewList] = useState([]);
    const [crewRoute, setCrewRoute] = useState(null);

    const handleOpenCrewList = useCallback(async (route) => {
        if (!canView) return;

        try {
            const url = `/legmembers/legmembersbyrouteno`;
            const routeDate = new Date(route.routeDate);
            const routeNo = route.routeNo;

            const payload = { routeDate, routeNo };      
            
            const res = await api.post(url, payload);

            const crews = Object.values((res.data || []).reduce((acc, i) => {
                if (!acc[i.crewCode]) acc[i.crewCode] = i;
                return acc;
            }, {})
            ).sort((a, b) => a.rankPriority - b.rankPriority);

            setCrewRoute(route);
            setCrewList(crews);
            setOpenDialog(true);
        } catch (e) {
            toast.error(e.message || "Failed to fetch crew list!");
        }
    }, [canView]);

    const rowProps = useMemo(() => ({
        timeMode,
        theme,
        canView,
        isMobile,
        minRouteTAT,
        routes: filteredRoutes,
        onOpenCrewList: handleOpenCrewList,
    }), [
        timeMode,
        theme,
        canView,
        isMobile,
        minRouteTAT,
        filteredRoutes,
        handleOpenCrewList,
    ]);


    return (
        <Box
            ref={listRef}
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
                overflowX: 'hidden',
            }}
        >
            <Box
                zIndex={9}
                position="sticky"
                top={0}
                pl={1}
                display="flex"
                alignItems="center"
                gap={1}
                borderBottom={`1px solid ${theme.palette.neutral.deep}`}
                bgcolor={theme.palette.background.default}
            >
                <WarningAmber />
                <Typography fontSize={12} fontWeight={500} py={2}>
                    AIRCRAFT CHANGE & TAT &lt; {minRouteTAT} MINS ({filteredRoutes.length})
                </Typography>
            </Box>

            {filteredRoutes.length > 0 ? (
                <List
                    height={listHeight}
                    rowCount={filteredRoutes.length}
                    rowHeight={rowHeight}
                    rowComponent={RowComponent}
                    rowProps={rowProps}
                />
            ) : (
                <FlexBetween p={1}>
                    <Typography variant="h6">
                        No crew routes with too short TAT found.
                    </Typography>
                </FlexBetween>
            )}

            {openDialog && (
                <RouteCrewListDialog
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    crewRoute={crewRoute}
                    crewList={crewList}
                />
            )}
        </Box>
    );
};

export default ShortTurnaroundCrewRoutes;
